import { useCallback, useEffect, useRef, useState } from "react";
import { apiService } from "../../api/apiService";
import { useAuth } from "../../hooks/useAuth";
import { KLUCZE_POWLOKI, pobierzZPamieci, tozsamoscKonta } from "../../hooks/usePamiecPowloki";

/**
 * TRYB TESTOWY POWIADOMIEŃ — jedno miejsce, z którego ekran czyta stan trybu (polecenie właściciela 2026-10-02).
 *
 * Zwraca `{ moj, lista, odswiez }`:
 *  • `moj`   — `{ aktywny, spolkaId, spolkaNazwa, od }` dla konta, które patrzy (baner nad nagłówkiem);
 *  • `lista` — stan wszystkich spółek (komunikat na /admin, suwaki w /admin/integrations); pobierana tylko
 *              na życzenie (`{ lista: true }`), bo zwykły pracownik dostałby na nią 403.
 *
 * ZASADA: EKRAN NIE ZGADUJE STANU, KTÓREGO NIE ZNA. Każde wywołanie stoi w try/catch; przy błędzie pole zostaje `null`.
 * Baner pracownika wtedy nic nie rysuje. Ekrany administratora odróżniają odmowę (403 — brak dostępu, cisza) od
 * błędu odczytu (`bladListy`): przy błędzie mówią wprost, że stanu nie znają — inaczej suwaki i komunikat znikałyby
 * bez słowa także wtedy, gdy tryb trwa.
 *
 * KIEDY PYTA SERWER PONOWNIE:
 *  • zdarzenie okna `tryb-testowy-powiadomien-zmiana` — most SignalR w NotificationContext (inna karta, inna osoba)
 *    oraz ten sam ekran zaraz po zapisie suwaka;
 *  • `realtime-reconnected` — po zerwanym połączeniu mogła nas ominąć zmiana;
 *  • powrót do karty (`visibilitychange`) — najczęściej raz na 60 s; to siatka bezpieczeństwa, nie odpytywanie w pętli;
 *  • co 5 minut przy widocznej karcie — dla karty bez połączenia na żywo i dla zmiany zrobionej z innej instancji.
 *
 * `moj` siedzi w pamięci powłoki (klucz `TRYB_TESTOWY`, tożsamość = id konta jak w Layout): skok w menu nie pyta
 * serwera drugi raz, a przelogowanie na tym samym stanowisku nie pokaże odpowiedzi poprzednika.
 */
export const ZDARZENIE_TRYBU_TESTOWEGO = "tryb-testowy-powiadomien-zmiana";

/** Najczęściej co ile powrót do karty może odpytać serwer. */
export const ODSTEP_PO_POWROCIE_MS = 60 * 1000;

/**
 * Rzadkie odpytanie przy WIDOCZNEJ karcie — siatka bezpieczeństwa dla karty, która zgubiła połączenie na żywo, i dla
 * zmiany zrobionej z innej instancji aplikacji na tej samej bazie (jej zdarzenie do tej karty nie dociera).
 */
export const ODSTEP_ODPYTANIA_MS = 5 * 60 * 1000;

/** Zdanie na ekranach administratora, gdy stanu trybu nie udało się pobrać (błąd sieci albo serwera — nie odmowa 403). */
export const KOMUNIKAT_BLEDU_ODCZYTU_TRYBU =
    "Nie udało się odczytać stanu trybu testowego powiadomień. Tryb może być włączony — spróbuj ponownie.";

/** Rozgłasza zmianę w TEJ karcie (po własnym zapisie) — inne karty dostają ją przez SignalR. */
export const ogloszZmianeTrybuTestowego = (szczegoly = null) => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent(ZDARZENIE_TRYBU_TESTOWEGO, { detail: szczegoly }));
};

/* JEDNO POBRANIE LISTY NA RAZ. Na /admin/integrations o listę pytają dwa ekrany naraz (strona i sekcja skrzynek),
   a każde pobranie to kilka zapytań do bazy. Wywołania, które przychodzą razem, dostają tę samą obietnicę:
    • zwykłe (wejście na ekran) dołącza do pobrania, które już trwa;
    • wymuszone (zdarzenie zmiany) dołącza TYLKO do pobrania rozpoczętego w tej samej chwili — nasłuchy jednego
      zdarzenia okna biegną jeden po drugim, zanim wykona się jakakolwiek mikrozadanie; pobranie starsze mogło
      wystartować przed zmianą, więc wymuszone go nie bierze. */
let listaWLocie = null;

export const pobierzListeWspolnie = (wymus = false) => {
    if (listaWLocie && (!wymus || listaWLocie.swieza)) return listaWLocie.obietnica;
    const wpis = { swieza: true, obietnica: null };
    const koniec = () => { if (listaWLocie === wpis) listaWLocie = null; };
    wpis.obietnica = Promise.resolve()
        .then(() => apiService.pobierzListeTrybuTestowego())
        .then((odp) => { koniec(); return odp?.data ?? null; }, (err) => { koniec(); throw err; });
    listaWLocie = wpis;
    Promise.resolve().then(() => { wpis.swieza = false; });
    return wpis.obietnica;
};

const useTrybTestowy = ({ lista: chceListe = false, moj: chceMoj = true } = {}) => {
    // Osłona: ekrany bywają renderowane z atrapą `useAuth`, która nie oddaje nic — hook nie ma prawa wysypać strony.
    const auth = useAuth();
    const idKonta = tozsamoscKonta(auth?.user);
    const [moj, setMoj] = useState(null);
    const [lista, setLista] = useState(null);
    const [bladListy, setBladListy] = useState(false);
    const zywy = useRef(true);
    const ostatnio = useRef(0);
    /* Numer żądania: odpowiedź, która wróciła PO nowszym pytaniu (wolne pierwsze pobranie kontra pobranie po
       zdarzeniu zmiany), nie może nadpisać świeżego stanu starym. Liczy się tylko najnowsze pytanie. */
    const nrMoj = useRef(0);
    const nrListy = useRef(0);

    useEffect(() => {
        zywy.current = true;
        return () => { zywy.current = false; };
    }, []);

    const wczytaj = useCallback(async (wymus = false) => {
        ostatnio.current = Date.now();
        const zadania = [];

        /* Bez znanego konta nie pytamy: tuż po wejściu powłoka bywa renderowana, zanim konto się wczyta, a pytanie
           „bez tożsamości” i drugie „z tożsamością” to dwa żądania o to samo (zapadka sufitZadanLandingow). */
        if (chceMoj && idKonta == null) {
            nrMoj.current += 1;
            if (zywy.current) setMoj(null);
        } else if (chceMoj) {
            nrMoj.current += 1;
            const nr = nrMoj.current;
            zadania.push((async () => {
                let dane = null;
                try {
                    dane = await pobierzZPamieci(KLUCZE_POWLOKI.TRYB_TESTOWY, {
                        pobieracz: () => apiService.pobierzMojTrybTestowy().then((r) => r?.data ?? null),
                        tozsamosc: idKonta,
                        wymus,
                    });
                } catch {
                    dane = null; // błąd albo odmowa — nic nie rysujemy
                }
                if (zywy.current && nr === nrMoj.current) setMoj(dane && typeof dane === "object" ? dane : null);
            })());
        }

        nrListy.current += 1;
        if (chceListe) {
            const nr = nrListy.current;
            zadania.push((async () => {
                let dane = null;
                let blad = false;
                try {
                    dane = await pobierzListeWspolnie(wymus);
                } catch (err) {
                    dane = null;
                    // 403 = brak dostępu do integracji (cisza); cokolwiek innego = stanu nie znamy i mówimy to.
                    blad = err?.response?.status !== 403;
                }
                if (zywy.current && nr === nrListy.current) {
                    setLista(dane && Array.isArray(dane.spolki) ? dane : null);
                    setBladListy(blad);
                }
            })());
        } else if (zywy.current) {
            setLista(null);
            setBladListy(false);
        }

        await Promise.all(zadania);
    }, [idKonta, chceListe, chceMoj]);

    useEffect(() => { wczytaj(false); }, [wczytaj]);

    useEffect(() => {
        if (typeof window === "undefined") return undefined;
        const odNowa = () => { wczytaj(true); };
        const poPowrocie = () => {
            if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
            if (Date.now() - ostatnio.current < ODSTEP_PO_POWROCIE_MS) return;
            wczytaj(true);
        };
        window.addEventListener(ZDARZENIE_TRYBU_TESTOWEGO, odNowa);
        window.addEventListener("realtime-reconnected", odNowa);
        document.addEventListener("visibilitychange", poPowrocie);
        // Rzadkie odpytanie: tylko przy widocznej karcie i tylko, gdy ostatnie pobranie jest starsze niż odstęp.
        const zegar = setInterval(() => {
            if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
            if (Date.now() - ostatnio.current < ODSTEP_ODPYTANIA_MS) return;
            wczytaj(true);
        }, ODSTEP_ODPYTANIA_MS);
        return () => {
            clearInterval(zegar);
            window.removeEventListener(ZDARZENIE_TRYBU_TESTOWEGO, odNowa);
            window.removeEventListener("realtime-reconnected", odNowa);
            document.removeEventListener("visibilitychange", poPowrocie);
        };
    }, [wczytaj]);

    const odswiez = useCallback(() => wczytaj(true), [wczytaj]);

    return { moj, lista, bladListy, odswiez };
};

/** Data „od” po polsku, krótko: „2.10, 14:05”. Pusta albo zepsuta → pusty napis. */
export const dataTrybuTestowego = (od) => {
    if (!od) return "";
    const d = new Date(od);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("pl-PL", { day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit" });
};

/**
 * Kalendarz: zdanie o zaproszeniach, które w trybie testowym NIE wyszły — albo `null`, gdy nikogo nie pominięto.
 * Serwer oddaje w odpowiedzi utworzenia wydarzenia pole `uczestnicyPominieci` (lista adresów albo liczba); pole jest
 * opcjonalne, więc jego brak znaczy „po staremu”.
 */
export const komunikatPominietychUczestnikow = (odpowiedz) => {
    const pominieci = odpowiedz?.uczestnicyPominieci;
    const ilu = Array.isArray(pominieci) ? pominieci.length : (Number(pominieci) > 0 ? Number(pominieci) : 0);
    return ilu > 0 ? `Tryb testowy powiadomień: zaproszenia nie zostały wysłane — pominięto ${ilu} uczestników` : null;
};

export default useTrybTestowy;
